import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { withRouter } from 'react-router'
import { useSelector } from 'hooks/redux'
import { getIsEditing } from 'selectors/navigator'
import { getInitialFeature } from 'selectors/feature/features'

import { setShowDrawerMask } from 'actions/drawer'
import {
  resetPreCreateNewFeature,
  createFeature,
} from 'actions/feature/features'
import { setSelectedIdWithDrawer } from 'actions/navigator'

import PropTypes from 'prop-types'
import ActionBar from 'components/ActionBar'
import InfoOverlay from 'components/InfoOverlay'
import strings from 'utils/strings'
import { insertIf } from 'utils/types'
import {
  CLEAR,
  DELETE,
  UNAVAILABLE,
  ACTIVATE,
  DEFAULT,
  CUSTOM,
} from 'constants/ui/editor'
import { isValidFeatureMetaData } from 'utils/feature'
import { OBSOLETE } from 'utils/constants.js'
import styled from '@emotion/styled'
import { EditorContent } from './Editor.style.js'

const Editor = ({
  toggleMarkForDeletion,
  deleteItem,
  clearItem,
  deleteHeaderText,
  clearHeaderText,
  deleteExplanation,
  clearExplanation,
  children,
  showDelete,
  showUnavailableAction,
  unavailableAction,
  unavailableHeaderText,
  unavailableExplanation,
  showActivateAction,
  showObsoleteAction,
  obsoleteAction,
  activateAction,
  activateHeaderText,
  activateExplanation,
  obsoleteHeaderText,
  obsoleteExplanation,
  onCancel,
  showClear,
  isInitial,
  history,
  customOverlayContent,
  showCustomOverlay,
  customActionButtons,
}) => {
  const isInitialMount = useRef(true)
  const dispatch = useDispatch()

  const [state, setState] = useState(DEFAULT)
  const setDefaultState = () => setState(DEFAULT)
  const showOverlay = useMemo(() => state !== DEFAULT, [state])

  const isEditing = useSelector(getIsEditing)
  const initialFeature = useSelector(getInitialFeature)

  useEffect(() => {
    if (showCustomOverlay) {
      setState(CUSTOM)
      dispatch(setShowDrawerMask(true))
    } else {
      setState(DEFAULT)
      dispatch(setShowDrawerMask(false))
    }
  }, [showCustomOverlay, dispatch])

  useEffect(() => {
    if (isInitialMount.current) isInitialMount.current = false
    else {
      if (showUnavailableAction) {
        setState(UNAVAILABLE)
        dispatch(setShowDrawerMask(true))
      }

      if (showActivateAction) {
        setState(ACTIVATE)
        dispatch(setShowDrawerMask(true))
      }

      if (showObsoleteAction) {
        setState(OBSOLETE)
        dispatch(setShowDrawerMask(true))
      }
    }
  }, [showUnavailableAction, showActivateAction, showObsoleteAction, dispatch])

  const handleDelete = useCallback(() => {
    deleteItem()
    dispatch(setShowDrawerMask(false))
    dispatch(setSelectedIdWithDrawer(null))
  }, [dispatch, deleteItem])

  const handleClear = useCallback(() => {
    clearItem()
    setDefaultState()
    dispatch(setShowDrawerMask(false))
  }, [dispatch, clearItem])

  const handleCancel = useCallback(() => {
    setDefaultState()
    dispatch(setShowDrawerMask(false))
    onCancel()
    if (toggleMarkForDeletion) {
      toggleMarkForDeletion(false)
    }
  }, [dispatch, toggleMarkForDeletion, onCancel])

  const handleUnavailableAction = useCallback(() => {
    unavailableAction(false)
    setDefaultState()
    dispatch(setShowDrawerMask(false))
  }, [dispatch, unavailableAction])

  const handleActivateAction = useCallback(() => {
    activateAction()
    setDefaultState()
    dispatch(setShowDrawerMask(false))
  }, [dispatch, activateAction])

  const handleObsoleteAction = useCallback(() => {
    obsoleteAction()
    setDefaultState()
    dispatch(setShowDrawerMask(false))
  }, [dispatch, obsoleteAction])

  const handleCancelCreate = useCallback(() => {
    dispatch(resetPreCreateNewFeature())
  }, [dispatch])

  const showDeleteOverlay = useCallback(() => {
    setState(DELETE)
    dispatch(setShowDrawerMask(true))
    toggleMarkForDeletion(true)
  }, [dispatch, toggleMarkForDeletion])

  const showClearOverlay = useCallback(() => {
    setState(CLEAR)
    dispatch(setShowDrawerMask(true))
  }, [dispatch])

  const handleCreate = useCallback(() => {
    const redirect = ({ currentFeature }) => {
      history.push({
        pathname: `/features/${Object.values(currentFeature.feature)[0].id}`,
        search: '?editing=true',
        state: {
          isNewFeature: true,
        },
      })
    }

    dispatch(createFeature(redirect))
  }, [dispatch, history])

  const actionBarButtons = () => {
    if (isInitial && !showCustomOverlay) {
      const { type, description, priority, releaseNumber, subGroup } =
        initialFeature
      const isValidNewFeature = isValidFeatureMetaData(
        type,
        priority,
        subGroup,
        releaseNumber,
        description
      )
      return [
        {
          text: strings.save,
          type: 'primary',
          action: handleCreate,
          disabled: !isValidNewFeature,
        },
        {
          text: strings.cancel,
          type: 'secondary',
          action: handleCancelCreate,
        },
      ]
    }

    return {
      [DEFAULT]: [
        ...insertIf(showDelete, {
          text: strings.delete,
          type: 'tertiary',
          action: showDeleteOverlay,
        }),
        ...insertIf(showClear, {
          text: strings.clear,
          type: 'secondary',
          action: showClearOverlay,
        }),
      ],
      [CLEAR]: [
        { text: strings.clear, type: 'danger', action: handleClear },
        {
          text: strings.cancel,
          type: 'secondary',
          action: handleCancel,
        },
      ],
      [DELETE]: [
        { text: strings.delete, type: 'danger', action: handleDelete },
        {
          text: strings.cancel,
          type: 'secondary',
          action: handleCancel,
        },
      ],
      [UNAVAILABLE]: [
        {
          text: strings.ok,
          type: 'primary',
          action: handleUnavailableAction,
        },
      ],
      [ACTIVATE]: [
        {
          text: strings.activate,
          type: 'primary',
          action: handleActivateAction,
        },
        {
          text: strings.cancel,
          type: 'secondary',
          action: handleCancel,
        },
      ],
      [OBSOLETE]: [
        {
          text: strings.make_obsolete,
          type: 'primary',
          action: handleObsoleteAction,
        },
        {
          text: strings.cancel,
          type: 'secondary',
          action: handleCancel,
        },
      ],
      [CUSTOM]: customActionButtons,
    }[state]
  }

  const overlayContent = {
    [DELETE]: {
      heading: deleteHeaderText,
      subheading: deleteExplanation,
    },
    [CLEAR]: {
      heading: clearHeaderText,
      subheading: clearExplanation,
    },
    [UNAVAILABLE]: {
      heading: unavailableHeaderText,
      subheading: unavailableExplanation,
    },
    [ACTIVATE]: {
      heading: activateHeaderText,
      subheading: activateExplanation,
    },
    [OBSOLETE]: {
      heading: obsoleteHeaderText,
      subheading: obsoleteExplanation,
    },
    [CUSTOM]: customOverlayContent,
  }

  const showActionBar = isEditing || state === ACTIVATE || state === OBSOLETE
  const infoOverlayClassname =
    state === ACTIVATE || state === OBSOLETE ? 'confirm-obsolete-overlay' : ''
  const actionButtonsClassName =
    state === OBSOLETE ? 'confirm-obsolete-footer' : ''
  return (
    <>
      {showOverlay ? (
        <InfoOverlay
          className={infoOverlayClassname}
          infoState={state}
          content={overlayContent}
        />
      ) : (
        <EditorContent>{children}</EditorContent>
      )}

      {showActionBar && (
        <StyledActionBar
          className={actionButtonsClassName}
          buttons={actionBarButtons()}
        />
      )}
    </>
  )
}

Editor.defaultProps = {
  showClear: true,
  showDelete: true,
  showUnavailableAction: false,
  showActivateAction: false,
  showObsoleteAction: false,
  onCancel: () => {},
}

Editor.propTypes = {
  toggleMarkForDeletion: PropTypes.func,
  deleteHeaderText: PropTypes.string,
  deleteExplanation: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  deleteItem: PropTypes.func,
  className: PropTypes.string,
  showClear: PropTypes.bool,
  showDelete: PropTypes.bool,
  clearItem: PropTypes.func,
  clearHeaderText: PropTypes.string,
  clearExplanation: PropTypes.string,
  showUnavailableAction: PropTypes.bool,
  unavailableAction: PropTypes.func,
  unavailableHeaderText: PropTypes.string,
  unavailableExplanation: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  showActivateAction: PropTypes.bool,
  showObsoleteAction: PropTypes.bool,
  activateAction: PropTypes.func,
  obsoleteAction: PropTypes.func,
  activateHeaderText: PropTypes.string,
  obsoleteHeaderText: PropTypes.string,
  activateExplanation: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  obsoleteExplanation: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  onCancel: PropTypes.func,
  isInitial: PropTypes.bool,
  history: PropTypes.object.isRequired,
  customOverlayContent: PropTypes.object,
  showCustomOverlay: PropTypes.bool,
  customActionButtons: PropTypes.array,
}

export default withRouter(Editor)

const StyledActionBar = styled(ActionBar)`
  margin-top: auto;
`
